import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AddressData,
  CprInfo,
  CprService,
  DavaAddressInfo,
  LibUtilityService,
  ResetPriceCalculator,
} from 'projects/shared/src/public-api';
import { ksfCustomerInfo } from 'projects/shared/src/lib/models/ksf.model';

@Component({
  selector: 'lib-ksf-customer-info',
  templateUrl: './ksf-customer-info.component.html',
  styleUrls: ['./ksf-customer-info.component.scss'],
  standalone: false,
})
export class KsfCustomerInfoComponent {
  // Event emitted to the parent component
  @Output() setCustomerInfoEvent = new EventEmitter<ksfCustomerInfo>();
  @Output() nextStepEvent = new EventEmitter();

  @Input() ksfPriceResult: any; // make interface
  @Input() textDirectedToCustomer: boolean = false;
  @Input() ksfCustomerInfo: ksfCustomerInfo = {
    cprNumber: '',
    firstname: '',
    lastname: '',
    selectedAddressInfo: {} as DavaAddressInfo,
    email: '',
    phoneNumber: '',
  };

  // Internal state variables
  errorInCprNumber: boolean = false;
  errorInFirstname: boolean = false;
  errorInLastname: boolean = false;
  errorInAddress: boolean = false;
  errorInEmail: boolean = false;
  errorInPhoneNumber: boolean = false;

  cprLoading: boolean = false;

  topFiveAddresses: DavaAddressInfo[] = [];
  showAddressOption: boolean = false;

  // ######################################

  seachManuelAddress: boolean = false;

  manuelAddress: {
    customerStreet: string;
    customerBuildingNumber: string;
    customerFloor: string | null;
    customerDoor: string | null;
    customerAdditionalCity: string | null;
    customerCity: string;
    customerPostCode: string;
  } = {
    customerStreet: '',
    customerBuildingNumber: '',
    customerFloor: null,
    customerDoor: null,
    customerAdditionalCity: null,
    customerCity: '',
    customerPostCode: '',
  };

  errorInCustomerStreet: boolean = false;
  errorInCustomerBuildingNumber: boolean = false;
  errorInCustomerFloor: boolean = false;
  errorInCustomerDoor: boolean = false;
  errorInCustomerAdditionalCity: boolean = false;
  errorInCustomerCity: boolean = false;
  errorInCustomerPostCode: boolean = false;

  addressData: AddressData | undefined = undefined;
  addressSearchText: string = '';

  // ######################################

  constructor(
    private libUtilityService: LibUtilityService,
    public resetPriceCalculator: ResetPriceCalculator,
    private cprService: CprService,
  ) {
    // Subscribe to reset events
    this.resetPriceCalculator.resetPriceCalculatorValues.subscribe(() => {
      this.resetKsfInfo();
    });

    this.resetPriceCalculator.resetKsfValues.subscribe(() => {
      this.resetKsfInfo();
    });
  }

  /**
   * Resets the KSF to the default state.
   * @returns void
   */
  resetKsfInfo(): void {
    this.errorInCprNumber = false;
    this.errorInFirstname = false;
    this.errorInLastname = false;
    this.errorInAddress = false;
    this.errorInEmail = false;
    this.errorInPhoneNumber = false;
    this.ksfCustomerInfo = {
      cprNumber: '',
      firstname: '',
      lastname: '',
      selectedAddressInfo: {} as DavaAddressInfo,
      email: '',
      phoneNumber: '',
    };
    this.cprLoading = false;
    this.topFiveAddresses = [];
    this.showAddressOption = false;
  }

  /**
   * Lookup customer information based on the provided CPR number.
   * Handles the process of fetching and updating customer information and addresses.
   * @returns void
   */
  lookupCPR(): void {
    // Return early if the CPR number is empty
    if (this.ksfCustomerInfo.cprNumber.trim().length === 0) {
      return;
    }

    // Set loading state for CPR lookup
    this.cprLoading = true;

    // Create an object with the CPR number for the lookup request
    const cprInfo: CprInfo = {
      cpr: this.ksfCustomerInfo.cprNumber,
    };

    // Make a request to lookup the CPR number
    this.cprService.lookupCPR(cprInfo).subscribe({
      next: (result: any) => {
        // Extract customer information from the response
        const customerInfo = result.data;
        this.ksfCustomerInfo.firstname = customerInfo.firstName;
        this.ksfCustomerInfo.lastname = customerInfo.lastName;

        if (customerInfo.address == '' && customerInfo.city == '') {
          this.libUtilityService.notification(
            'warrning',
            'Info',
            'Vi kunne ikke finde nogle informationer på CPR-Nummeret - Indtast addresse manuelt',
          );
          this.cprLoading = false;

          return;
        }

        // Reset any CPR error flags and stop the loading state
        this.errorInCprNumber = false;
        this.cprLoading = false;
      },
      error: (error: any) => {
        // Handle errors in CPR lookup and set error flags
        this.cprLoading = false;
        this.errorInCprNumber = true;
        console.error(error);
        return;
      },
    });
  }

  /**
   * Resets all customer info error flags.
   * @returns void
   */
  resetCustomerInfoErrorFlags(): void {
    this.errorInCprNumber = false;
    this.errorInFirstname = false;
    this.errorInLastname = false;
    this.errorInAddress = false;
    this.errorInEmail = false;

    this.errorInCustomerStreet = false;
    this.errorInCustomerBuildingNumber = false;
    this.errorInCustomerFloor = false;
    this.errorInCustomerDoor = false;
    this.errorInCustomerAdditionalCity = false;
    this.errorInCustomerCity = false;
    this.errorInCustomerPostCode = false;
    this.errorInPhoneNumber = false;
  }

  clearManualAddressError(field: string): void {
    switch (field) {
      case 'customerStreet':
        this.errorInCustomerStreet = false;
        break;
      case 'customerBuildingNumber':
        this.errorInCustomerBuildingNumber = false;
        break;
      case 'customerFloor':
        this.errorInCustomerFloor = false;
        break;
      case 'customerDoor':
        this.errorInCustomerDoor = false;
        break;
      case 'customerAdditionalCity':
        this.errorInCustomerAdditionalCity = false;
        break;
      case 'customerCity':
        this.errorInCustomerCity = false;
        break;
      case 'customerPostCode':
        this.errorInCustomerPostCode = false;
        break;
      default:
        break;
    }
  }

  /**
   * Validate customer information for completeness and correctness.
   * @returns void
   */
  validateCustomerInfo(): void {
    this.resetCustomerInfoErrorFlags();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validate each field and set corresponding error flags if invalid
    if (
      !this.ksfCustomerInfo.cprNumber.trim() ||
      this.ksfCustomerInfo.cprNumber.trim().length < 10 ||
      !this.ksfCustomerInfo.firstname.trim() ||
      !this.ksfCustomerInfo.lastname.trim() ||
      (!this.addressData && !this.seachManuelAddress) ||
      !this.ksfCustomerInfo.email.trim() ||
      !emailRegex.test(this.ksfCustomerInfo.email.trim()) ||
      !this.ksfCustomerInfo.phoneNumber ||
      this.ksfCustomerInfo.phoneNumber.toString().length !== 8 ||
      (!this.manuelAddress.customerStreet && this.seachManuelAddress) ||
      (!this.manuelAddress.customerBuildingNumber && this.seachManuelAddress) ||
      (!this.manuelAddress.customerCity && this.seachManuelAddress) ||
      (!this.manuelAddress.customerPostCode && this.seachManuelAddress)
    ) {
      this.errorInCprNumber =
        !this.ksfCustomerInfo.cprNumber.trim() ||
        this.ksfCustomerInfo.cprNumber.trim().length < 10;
      this.errorInFirstname = !this.ksfCustomerInfo.firstname.trim();
      this.errorInLastname = !this.ksfCustomerInfo.lastname.trim();
      this.errorInAddress = !this.addressData && !this.seachManuelAddress;
      this.errorInEmail =
        !this.ksfCustomerInfo.email.trim() ||
        !emailRegex.test(this.ksfCustomerInfo.email.trim());
      this.errorInPhoneNumber =
        !this.ksfCustomerInfo.phoneNumber ||
        this.ksfCustomerInfo.phoneNumber.toString().length !== 8;

      this.errorInCustomerStreet =
        !this.manuelAddress.customerStreet && this.seachManuelAddress;

      this.errorInCustomerBuildingNumber =
        !this.manuelAddress.customerBuildingNumber && this.seachManuelAddress;

      this.errorInCustomerCity =
        !this.manuelAddress.customerCity && this.seachManuelAddress;

      this.errorInCustomerPostCode =
        !this.manuelAddress.customerPostCode && this.seachManuelAddress;

      return;
    }

    // Trim and format customer information for final submission

    if (!this.seachManuelAddress) {
      this.ksfCustomerInfo = {
        cprNumber: this.ksfCustomerInfo.cprNumber.trim(),
        firstname: this.ksfCustomerInfo.firstname.trim(),
        lastname: this.ksfCustomerInfo.lastname.trim(),
        selectedAddressInfo: this.ksfCustomerInfo.selectedAddressInfo,
        email: this.ksfCustomerInfo.email.trim(),
        phoneNumber: this.ksfCustomerInfo.phoneNumber.toString(),
      };
    } else {
      const davaAddressInfo: DavaAddressInfo = {
        tekst: '',
        adresse: {
          id: '',
          status: 0,
          darstatus: 0,
          vejkode: '',
          vejnavn: this.manuelAddress.customerStreet.trim(),
          adresseringsvejnavn: '',
          husnr: this.manuelAddress.customerBuildingNumber.trim(),
          etage: this.manuelAddress.customerFloor
            ? this.manuelAddress.customerFloor.trim()
            : null,
          dør: this.manuelAddress.customerDoor
            ? this.manuelAddress.customerDoor.trim()
            : null,
          supplerendebynavn: this.manuelAddress.customerAdditionalCity
            ? this.manuelAddress.customerAdditionalCity.trim()
            : null,
          postnr: this.manuelAddress.customerPostCode,
          postnrnavn: this.manuelAddress.customerCity.trim(),
          stormodtagerpostnr: null,
          stormodtagerpostnrnavn: null,
          kommunekode: '',
          adgangsadresseid: '',
          x: 0,
          y: 0,
          href: '',
        },
      };

      //selectedAddressInfo
      this.ksfCustomerInfo.selectedAddressInfo = davaAddressInfo;
    }

    // Emit validated customer information and proceed to the next step
    this.setCustomerInfoEvent.emit(this.ksfCustomerInfo);
    this.nextStepEvent.emit();
  }

  /**
   * Set the selected address from autocomplete options.
   * Updates the customer information with the chosen address and hides the address options.
   * @param address - The selected address from the autocomplete options.
   * @returns void
   */
  setAddress(address: AddressData): void {
    if (!address) return;
    this.addressData = address;

    const customerStreet = !this.seachManuelAddress
      ? (address?.vejnavn ?? '')
      : this.manuelAddress.customerStreet;

    const customerBuildingNumber = !this.seachManuelAddress
      ? (address?.husnr ?? '')
      : this.manuelAddress.customerBuildingNumber;

    const customerFloor = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerFloor;

    const customerDoor = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerDoor;

    const customerAdditionalCity = !this.seachManuelAddress
      ? null
      : this.manuelAddress.customerAdditionalCity;

    const customerCity = !this.seachManuelAddress
      ? (address?.postnrnavn ?? '')
      : this.manuelAddress.customerCity;

    const customerPostCode = !this.seachManuelAddress
      ? (address?.postnr ?? '')
      : this.manuelAddress.customerPostCode;

    const davaAddressInfo: DavaAddressInfo = {
      tekst: '',
      adresse: {
        id: '',
        status: 0,
        darstatus: 0,
        vejkode: '',
        vejnavn: customerStreet,
        adresseringsvejnavn: '',
        husnr: customerBuildingNumber,
        etage: customerFloor,
        dør: customerDoor,
        supplerendebynavn: customerAdditionalCity,
        postnr: customerPostCode,
        postnrnavn: customerCity,
        stormodtagerpostnr: null,
        stormodtagerpostnrnavn: null,
        kommunekode: '',
        adgangsadresseid: '',
        x: 0,
        y: 0,
        href: '',
      },
    };

    this.ksfCustomerInfo.selectedAddressInfo = davaAddressInfo;
  }
}
